import React from 'react';
import { FiTrash2 } from 'react-icons/fi';
import { Toggle } from '../../../../components';
import { useViewerStore } from "../../../../store/store";
import { Alert, Button, Divider, Flex, InputNumber } from "antd";
import { SLIDER_PROPERTIES_SETTINGS } from "../../../../Constants";
import { ArrowsOutCardinal, Plus } from "@phosphor-icons/react";
import {
    DeletePlaneRoof,
    UpdatePlaneRoof
} from "../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/PlaneRoofControls";
import { syncLightReachSitePitchWarning } from "../../../../helpers/lightReachWarning";

const SurfaceProperties: React.FC = () => {
    const sceneViewer = useViewerStore(state => state.sceneViewer);

    const flatRoofHeight = useViewerStore(state => state.selectedFlatRoofHeight);
    const flatRoofAzimuth = useViewerStore(state => state.selectedFlatRoofAzimuth);
    const flatRoofPitch = useViewerStore(state => state.selectedFlatRoofPitch);
    const flatRoofScaleX = useViewerStore(state => state.selectedFlatRoofScaleX);
    const flatRoofScaleY = useViewerStore(state => state.selectedFlatRoofScaleY);
    const flatRoofIsCarport = useViewerStore(state => state.selectedFlatRoofIsCarport);
    const {
        ROOF_HEIGHT: settingsRoofHeight,
        AZIMUTH: settingsAzimuth,
        PITCH: settingsPitch,
        SCALE: settingsScale
    } = SLIDER_PROPERTIES_SETTINGS.GROUND_MOUNT_PROPERTIES;

    const setCreatePlaneMode = () => {
        const { createPlaneMode } = sceneViewer;
        if (!createPlaneMode) {
            sceneViewer.viewerAlert({
                show: true,
                isModal: false,
                title: 'Add Ground Mount',
                message: `Click  a point on the imagery to create a ground mount`,
                isCloseAfterDisplay: true,
            });
        } else {
            sceneViewer.viewerAlert({ show: false });
        }
        sceneViewer.setCreatePlaneMode(!createPlaneMode);
    };

    const deleteFlatRoof = () => {
        DeletePlaneRoof(sceneViewer);
    };

    const onChangeCarport = () => {
        useViewerStore.setState({selectedFlatRoofIsCarport : !flatRoofIsCarport});
        UpdatePlaneRoof(sceneViewer, "carport", !flatRoofIsCarport);
    };

    const limitToTwoDecimalPlaces = (value: number): number => {
        return Number(value.toFixed(2)) || 0;
    };

    const validateInput = (value: number, min: number, max: number, property: string): boolean => {

        if (value < min || value > max) {
            sceneViewer.viewerAlert({
                show: true,
                isModal: false,
                title: 'Range Error',
                message: `Cannot set given value, ${property} should be between ${min} and ${max}`,
                messageType: 'error',
                isCloseAfterDisplay: true,
            });
            return false;
        }
        return true;
    };

    const onBlurHeightOfFlatRoof = (value: number | null) => {
        if (value === null || !validateInput(value, settingsRoofHeight.min, settingsRoofHeight.max, 'Roof Height')) return;

        const parsedValue = limitToTwoDecimalPlaces(value);
        useViewerStore.setState({selectedFlatRoofHeight : parsedValue});
        UpdatePlaneRoof(sceneViewer, "height", parsedValue * 0.3048);
    };

    const onBlurAzimuthOfFlatRoof = (value: number | null) => {
        if (value === null || !validateInput(value, settingsAzimuth.min, settingsAzimuth.max, 'Azimuth')) return;

        const parsedValue = limitToTwoDecimalPlaces(value);
        useViewerStore.setState({selectedFlatRoofAzimuth : parsedValue});
        UpdatePlaneRoof(sceneViewer, "azimuth", parsedValue);
    };

    const onBlurPitchOfFlatRoof = (value: number | null) => {
        if (value === null || !validateInput(value, settingsPitch.min, settingsPitch.max, 'Pitch')) return;

        const parsedValue = limitToTwoDecimalPlaces(value);
        useViewerStore.setState({selectedFlatRoofPitch : parsedValue});
        UpdatePlaneRoof(sceneViewer, "pitch", parsedValue);
        syncLightReachSitePitchWarning(sceneViewer);
    };

    const onBlurScaleXofFlatRoof = (value: number | null) => {
        if (value === null || !validateInput(value, settingsScale.X.min, settingsScale.X.max, 'Length')) return;

        const parsedValue = limitToTwoDecimalPlaces(value);
        useViewerStore.setState({selectedFlatRoofScaleX : parsedValue});
        UpdatePlaneRoof(sceneViewer, "sclX", parsedValue * 0.3048);
    };

    const onBlurScaleYofFlatRoof = (value: number | null) => {
        if (value === null || !validateInput(value, settingsScale.Y.min, settingsScale.Y.max, 'Breadth')) return;

        const parsedValue = limitToTwoDecimalPlaces(value);
        useViewerStore.setState({selectedFlatRoofScaleY : parsedValue});
        UpdatePlaneRoof(sceneViewer, "sclY", parsedValue * 0.3048);
    };

    const moveFlatRoof = () => {
        if (!sceneViewer.selFlatRoof) {
            sceneViewer.viewerAlert({
                show: true,
                isModal: false,
                title: 'Move Ground Mount',
                message: `Select a ground mount to move`,
                isCloseAfterDisplay: true,
            });
            return;
        }
        sceneViewer.transform.attach(sceneViewer.selFlatRoof);
    };

    const blurInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        (e.target as HTMLInputElement).blur();
    };


    return (
        <div className='tab__inner'>
            <div className='tab__title'>
                <span className='tab__title--text'>Ground Mount Properties</span>
                <div className='tab__title--btns'>
                    <FiTrash2 onClick={deleteFlatRoof} />
                </div>
            </div>
            <div className='tab__column'>
                <div className='tab__flex'>
                    <Button icon={<Plus size={18} />} onClick={setCreatePlaneMode} size='middle'>Add</Button>
                    <Button icon={<ArrowsOutCardinal size={18} />} onClick={moveFlatRoof} size='middle'>Move</Button>
                </div>
            </div>

            <div className="tab__column" id='settingFlatRoof'>
                <Flex align={'flex-start'} vertical>
                    <span className="tab__title--text">height</span>
                    <br/>
                    <Flex vertical>
                        <span className="tab__column--subtitle">Roof Height</span>
                        <Flex align="center" gap={5}>
                            <InputNumber
                                min={settingsRoofHeight.min}
                                max={settingsRoofHeight.max}
                                value={limitToTwoDecimalPlaces(flatRoofHeight)}
                                onChange={(value) => useViewerStore.setState({selectedFlatRoofHeight : value ?? 0})}
                                onBlur={(e) => onBlurHeightOfFlatRoof(Number((e.target as HTMLInputElement).value))}
                                onPressEnter={blurInput}
                            />
                            <span className={"flatRoofPropertiesUnitText"}>ft</span>
                        </Flex>
                    </Flex>

                    <Divider />

                    <span className="tab__title--text">Angle</span>
                    <br/>
                    <Flex align='center' gap={10} style={{ width: '100%' }}>
                        <Flex vertical>
                            <span className="tab__column--subtitle">Pitch</span>
                            <Flex align="flex-start" gap={5}>
                                <InputNumber
                                    min={settingsPitch.min}
                                    max={settingsPitch.max}
                                    value={limitToTwoDecimalPlaces(flatRoofPitch)}
                                    onBlur={(e) => onBlurPitchOfFlatRoof(Number((e.target as HTMLInputElement).value))}
                                    onPressEnter={blurInput}
                                />
                                <span className={"flatRoofPropertiesUnitText"}>&deg;</span>
                            </Flex>
                        </Flex>

                        <Flex vertical>
                            <span className="tab__column--subtitle">Azimuth</span>
                            <Flex align="flex-start" gap={5}>
                                <InputNumber
                                    min={settingsAzimuth.min}
                                    max={settingsAzimuth.max}
                                    value={limitToTwoDecimalPlaces(flatRoofAzimuth)}
                                    onBlur={(e) => onBlurAzimuthOfFlatRoof(Number((e.target as HTMLInputElement).value))}
                                    onPressEnter={blurInput}
                                />
                                <span className={"flatRoofPropertiesUnitText"}>&deg;</span>
                            </Flex>
                        </Flex>
                    </Flex>

                    <Divider />

                    <span className="tab__title--text">Dimension</span>
                    <br/>
                    <Flex align='center' gap={10} style={{ width: '100%' }}>
                        <Flex vertical>
                            <label className='tab__column--subtitle'>Length</label>
                            <Flex align="center" gap={5}>
                                <InputNumber
                                    min={settingsScale.X.min}
                                    max={settingsScale.X.max}
                                    value={limitToTwoDecimalPlaces(flatRoofScaleX)}
                                    onBlur={(e) => onBlurScaleXofFlatRoof(Number((e.target as HTMLInputElement).value))}
                                    onPressEnter={blurInput}
                                />
                                <span className={"flatRoofPropertiesUnitText"}>ft</span>
                            </Flex>
                        </Flex>

                        <Flex vertical>
                            <label className='tab__column--subtitle'>Width</label>
                            <Flex align="center" gap={5}>
                                <InputNumber
                                    min={settingsScale.Y.min}
                                    max={settingsScale.Y.max}
                                    value={limitToTwoDecimalPlaces(flatRoofScaleY)}
                                    onBlur={(e) => onBlurScaleYofFlatRoof(Number((e.target as HTMLInputElement).value))}
                                    onPressEnter={blurInput}
                                />
                                <span className={"flatRoofPropertiesUnitText"}>ft</span>
                            </Flex>
                        </Flex>
                    </Flex>

                    <Divider />

                    <Flex vertical>
                        <span className="tab__title--text">Type</span>
                        <label className='tab__column--subtitle'>Carport</label>
                        <Toggle on={flatRoofIsCarport} onChange={onChangeCarport} />
                    </Flex>

                    <Divider />
                    <Alert
                        style={{ color: "black" }}
                        message="Note : Updating any property will reset the modules on the selected flat roof"
                        type="info"
                        showIcon
                    />
                </Flex>
            </div>
        </div>
    );
};

export default SurfaceProperties;